import { useEffect, useState } from "react";
import axios from "axios";
import { AlertCircle, X, ChevronRight } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ALERT_HOUR_EST = 9; // only show alert after 9:00 AM EST

/**
 * Banner that lists groups in the current user's scope that have NOT logged in today.
 * - Admin: all groups across all divisions.
 * - Supervisor: only their division's groups (server-enforced).
 *
 * Hidden when:
 *   - today is not a camp day,
 *   - current EST hour < 9,
 *   - all groups have logged in,
 *   - user has dismissed the banner (per-session, not persisted).
 */
export default function MissingLoginAlert({ onClickGroup }) {
  const [data, setData] = useState(null);
  const [dismissed, setDismissed] = useState(false);

  const load = async () => {
    try {
      const r = await axios.get(`${API}/login-sessions/missing-today`);
      setData(r.data || null);
    } catch (_e) {
      setData(null);
    }
  };

  useEffect(() => {
    load();
    const id = setInterval(load, 5 * 60 * 1000); // refresh every 5 min
    return () => clearInterval(id);
  }, []);

  if (dismissed || !data) return null;
  if (!data.is_camp_day) return null;
  if (!data.count || data.count === 0) return null;

  // Only show after 9 AM EST
  const hour = parseInt((data.current_time_est || "00:00").split(":")[0], 10);
  if (Number.isFinite(hour) && hour < ALERT_HOUR_EST) return null;

  return (
    <div
      className="bg-rose-50 border-2 border-rose-300 rounded-2xl p-4 shadow-sm"
      data-testid="missing-login-alert"
    >
      <div className="flex items-start gap-3">
        <div className="flex-shrink-0 w-10 h-10 rounded-full bg-rose-200 flex items-center justify-center">
          <AlertCircle className="w-5 h-5 text-rose-700" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <div>
              <p className="text-sm font-black text-rose-900 uppercase tracking-wide">
                {data.count} {data.count === 1 ? "group hasn't" : "groups haven't"} logged in today
              </p>
              <p className="text-xs text-rose-700 mt-0.5">
                As of {data.current_time_est} EST · {data.total_in_scope} groups in scope · daily login is required to mark attendance, buddy sheets, and lunch
              </p>
            </div>
            <button
              type="button"
              onClick={() => setDismissed(true)}
              className="text-rose-400 hover:text-rose-700 p-1 rounded"
              data-testid="missing-login-alert-dismiss"
              aria-label="Dismiss"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          <div className="mt-3 flex flex-wrap gap-1.5" data-testid="missing-login-alert-codes">
            {data.missing.map((g) => (
              <button
                key={g.group_code}
                type="button"
                onClick={() => onClickGroup && onClickGroup(g.group_code)}
                className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg bg-white border border-rose-200 text-xs font-bold text-rose-800 hover:bg-rose-100 hover:border-rose-400 transition-colors"
                data-testid={`missing-login-pill-${g.group_code}`}
                title={g.group_name || g.group_code}
              >
                {g.group_code}
                {onClickGroup && <ChevronRight className="w-3 h-3" />}
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
